/**
 * Products by solution type on a price line. Mixed units are not one axis, so
 * "per seat" and "per location" sit side by side with the unit on each chip
 * rather than being normalised into a lie.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CARD } from './tableLayout';
import type { BoardroomPriceMap, BoardroomPriceProduct } from '../../../types';
import MissingList, { type OnDig } from './MissingList';
import DataTable from './DataTable';
import type { Column } from './tableColumns';
import { useReportSeen } from '../useSeen';
import NewHere from './NewHere';
import { MAX_ZOOM, STACK_STEP, lensAt, lensLayout, nudgeAway } from './priceLens';

function money(n: number): string {
  return Number.isInteger(n) ? `$${n.toLocaleString()}` : `$${n.toFixed(2)}`;
}

/**
 * The price line: one dot per plan with a monthly figure.
 *
 * HOVER TO MAGNIFY. Plans bunch up at the cheap end until they are one orange
 * smear, so the line spreads the dots near the cursor apart and grows them
 * (see `priceLens`), and stacks the ones that share a price. The dot under the
 * pointer is named below the line; clicking it takes you to its row.
 *
 * THE LENS HOLDS STILL WHILE YOU AIM. It used to sit wherever the pointer was,
 * so every move of the mouse moved the dots too: a dot near the edge of the
 * lens slid away as you reached for it, and you had to chase it. Now the lens
 * centres where the pointer comes in and stays there while the pointer is
 * inside it. Only when the pointer passes its edge does it move to the
 * pointer, and there, outside the lens, dots sit at their true place, so the
 * dot under the pointer does not move when the lens does.
 *
 * The dot you are on also gets a little room: its close neighbours step a few
 * pixels away (`nudgeAway`) and come back when you move off it.
 *
 * SCROLL TO ZOOM. Plans a dollar or two apart still sit on each other under
 * the plain lens, so scrolling up over the line magnifies harder (`lensAt`),
 * and scrolling down eases it back. The page only stops scrolling while the
 * zoom has somewhere to go: scrolling down at no zoom, or up at full zoom,
 * scrolls the page as usual. Leaving the line resets it.
 */
function Strip({
  products,
  max,
  onPick,
}: {
  products: BoardroomPriceProduct[];
  max: number;
  onPick: (productId: number) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [cursor, setCursor] = useState<number | null>(null);
  const [width, setWidth] = useState(0);
  const [hovered, setHovered] = useState<number | null>(null);
  // The name under the line stays on the last dot you were on, so it does not
  // blink out in the gaps between dots.
  const [named, setNamed] = useState<number | null>(null);
  const [zoom, setZoom] = useState(1);
  const lens = lensAt(zoom);

  const plans = useMemo(
    () =>
      products.flatMap(p =>
        p.plans
          .filter(pl => pl.monthly !== null)
          .map(pl => ({
            id: pl.id,
            product: p,
            plan: pl,
            at: max > 0 ? (pl.monthly as number) / max : 0,
          })),
      ),
    [products, max],
  );
  const dots = useMemo(
    () =>
      lensLayout(plans.map(p => ({ id: p.id, at: p.at })), cursor, width, {
        dot: DOT,
        radius: lens.radius,
        strength: lens.strength,
      }),
    [plans, cursor, width, lens.radius, lens.strength],
  );
  const nudges = useMemo(() => nudgeAway(dots, hovered, { dot: DOT }), [dots, hovered]);
  const byId = useMemo(() => new Map(plans.map(p => [p.id, p])), [plans]);
  const lit = named !== null ? byId.get(named) ?? null : null;
  const open = cursor !== null;
  // Tall enough for the highest stack the lens has made, so a pile of plans at
  // one price is not cut off at the top.
  const tallest = dots.reduce((m, d) => Math.max(m, d.level), 0);
  const openHeight = Math.max(72, 4 + tallest * DOT * STACK_STEP + DOT * 1.6 + 8);

  // React's onWheel cannot stop the page scrolling, so the wheel is listened to
  // directly. Read through a ref, so the listener is added once.
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const now = zoomRef.current;
      const next = Math.max(1, Math.min(MAX_ZOOM, now * Math.exp(-e.deltaY * 0.01)));
      if (next === now) return;
      e.preventDefault();
      setZoom(next);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const track = (clientX: number) => {
    const box = ref.current?.getBoundingClientRect();
    if (!box) return;
    setWidth(box.width);
    const x = Math.max(0, Math.min(box.width, clientX - box.left));
    setCursor(at => (at === null || Math.abs(x - at) >= lens.radius ? x : at));
  };

  return (
    /* THE ENDS OF THE LINE SIT IN FLOW, not hanging below it. They used to be
       absolutely positioned at `-bottom-4` — sixteen pixels outside a box with
       eight of margin — so they had always overflowed onto whatever came next.
       That was empty header space until the table grew a toolbar, and then
       "$2,999/mo" was printed underneath the CSV button. */
    <div className="mt-1 mb-2">
      <div
        ref={ref}
        data-testid="price-strip"
        onMouseMove={e => track(e.clientX)}
        onMouseLeave={() => {
          setCursor(null);
          setHovered(null);
          setNamed(null);
          setZoom(1);
        }}
        className="relative border-b border-gray-200 dark:border-gray-700 transition-[height] duration-150 ease-out motion-reduce:transition-none"
        style={{ height: open ? openHeight : 24 }}
      >
        {dots.map(d => {
          const p = byId.get(d.id)!;
          const size = DOT * d.scale;
          const nudge = nudges.get(d.id);
          return (
            <button
              key={d.id}
              type="button"
              tabIndex={-1}
              aria-label={`${p.product.title} · ${p.plan.title} · ${p.plan.label}`}
              title={`${p.product.title} · ${p.plan.title} · ${p.plan.label}`}
              data-testid="price-dot"
              onMouseEnter={() => {
                setHovered(d.id);
                setNamed(d.id);
              }}
              onMouseLeave={() => setHovered(h => (h === d.id ? null : h))}
              onClick={() => onPick(p.product.id)}
              className={`absolute -translate-x-1/2 rounded-full transition-all duration-150 ease-out motion-reduce:transition-none ${
                hovered === d.id
                  ? 'bg-orange-500 ring-2 ring-orange-200 dark:ring-orange-900 z-10'
                  : 'bg-orange-400'
              }`}
              style={{
                // At rest, a percentage, so the line follows the card's width
                // with no measuring; under the lens, the pixels it worked out.
                left: open ? d.x + (nudge?.dx ?? 0) : `${p.at * 100}%`,
                bottom: Math.max(0, d.level * (DOT * STACK_STEP) + (open ? 4 : 0) + (nudge?.dy ?? 0)),
                width: size,
                height: size,
              }}
            />
          );
        })}
      </div>
      <div className="flex justify-between gap-2 text-[10px] text-gray-400">
        <span>$0/mo</span>
        {open && !lit && (
          <span className="text-[10px] text-gray-400" data-testid="price-zoom-hint">
            {zoom > 1.05 ? `zoomed ${zoom.toFixed(1)}× - scroll down to ease off` : 'scroll up to zoom in'}
          </span>
        )}
        {lit && (
          <span className="min-w-0 truncate text-xs text-gray-700 dark:text-gray-200" data-testid="price-dot-name">
            {lit.product.title} · {lit.plan.title} · {lit.plan.label}
            <span className="text-gray-400"> - click to find it in the table</span>
          </span>
        )}
        <span>{money(max)}/mo</span>
      </div>
    </div>
  );
}

const DOT = 8;
/** How long a row picked from the line stays lit. Matches `.jc-pinpoint`. */
const PINPOINT_MS = 2600;

/** ONE DESCRIPTION PER COLUMN: what it draws, what it means, how wide. */
export function priceColumns(
  onOpen: (id: number) => void,
  unit: string,
): Column<BoardroomPriceProduct>[] {
  return [
    {
      key: 'product',
      label: 'product',
      width: 30,
      always: true,
      value: p => p.title,
      cell: p => (
        <>
          <button type="button" onClick={() => onOpen(p.id)} className="text-gray-800 dark:text-gray-200 hover:underline text-left">
            {p.title}
            {p.verdict === 'HOLDS' && <span className="text-emerald-500"> ✓</span>}
          </button>
          {/* THE MAKER, REACHABLE. It carried its id all along and rendered as
              grey text — the one thing on the row you could read and not open. */}
          {p.company && (
            <div className="text-[11px]">
              <button
                type="button"
                onClick={() => onOpen(p.company!.id)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:underline text-left"
              >
                {p.company.title}
              </button>
            </div>
          )}
        </>
      ),
    },
    {
      key: 'plans',
      label: 'plans',
      width: 45,
      sortable: false,
      value: p => p.plans.map(pl => `${pl.title}: ${pl.label}`).join(' / '),
      cell: p => (
        <div className="flex flex-wrap gap-1">
          {p.plans.length === 0 &&
            (p.pricing_settled ? (
              // SOMEBODY LOOKED AND THERE WAS NOTHING TO FIND. That is a
              // finding about how they sell — you have to ring them — and it
              // used to read identically to a product nobody had opened, so a
              // table that had been worked through still looked untouched.
              <span
                className="italic text-gray-500 dark:text-gray-400"
                title="Checked: this one does not publish a price. Nothing left to look up."
              >
                none published
              </span>
            ) : (
              <span className="text-gray-400 italic">no pricing yet</span>
            ))}
          {p.plans.map(pl => (
            <button
              key={pl.id}
              type="button"
              onClick={() => onOpen(pl.id)}
              title={pl.observed_at ? `seen ${pl.observed_at}` : undefined}
              className={`px-1.5 py-0.5 rounded-full border text-xs ${
                unit && pl.unit === unit ? 'border-orange-400' : 'border-gray-200 dark:border-gray-700'
              } text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20`}
            >
              {pl.title} · {pl.label}
            </button>
          ))}
        </div>
      ),
    },
    {
      // Sorting by the NUMBER, not by the words: "$5–20/mo" beside "free"
      // orders by what it costs rather than by how it is spelled.
      key: 'from',
      label: 'from /month',
      width: 25,
      align: 'right',
      value: p => p.from_monthly,
      cell: p => (
        <span className="text-xs font-mono text-gray-500 whitespace-nowrap">
          {p.from_monthly !== null
            ? p.from_monthly === p.to_monthly
              ? `${money(p.from_monthly)}/mo`
              : `${money(p.from_monthly)}–${money(p.to_monthly as number)}/mo`
            : p.cheapest_label ?? ''}
        </span>
      ),
    },
  ];
}

export default function PriceMapReport({
  data,
  onOpen,
  onDig,
  digging,
  unseen,
  onSeen,
  roomName,
}: {
  data: BoardroomPriceMap;
  onOpen: (itemId: number) => void;
  onDig: OnDig;
  digging: boolean;
  /** Ids that changed and have not been looked at. */
  unseen?: ReadonlySet<number>;
  /** Ids now on screen — the caller drops them from the badge. */
  onSeen?: (ids: number[]) => void;
  roomName?: string;
}) {
  const [unit, setUnit] = useState<string>('');
  const columns = useMemo(() => priceColumns(onOpen, unit), [onOpen, unit]);
  const units = Array.from(new Set(data.groups.flatMap(g => g.unit_mix))).sort();
  const empty = data.totals.products === 0;
  const keep = (p: BoardroomPriceProduct) => !unit || p.plans.some(pl => pl.unit === unit);
  // ONE TRACKER over a table per solution type plus the questions under them.
  // A tracker each and the first table to render pruned the marks belonging to
  // every other group, then the questions list pruned what was left.
  const seen = useReportSeen(unseen, onSeen, [
    ...data.groups.flatMap(g => g.products.filter(keep).map(p => p.id)),
    ...data.missing.map(m => m.item_id),
  ]);

  // FROM A DOT TO ITS ROW. Scroll the row into view and light it — a violet
  // pulse, so it cannot be mistaken for the amber "new" or sky "updated"
  // marks. A flipped table has no row per product; open the product instead.
  const body = useRef<HTMLDivElement | null>(null);
  const pick = useCallback(
    (productId: number) => {
      const row = body.current?.querySelector<HTMLElement>(
        `[data-testid="price-row-${productId}"]`,
      );
      if (!row) {
        onOpen(productId);
        return;
      }
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      row.classList.remove('jc-pinpoint');
      // Read layout so a second click on the same row restarts the pulse.
      void row.offsetWidth;
      row.classList.add('jc-pinpoint');
      window.setTimeout(() => row.classList.remove('jc-pinpoint'), PINPOINT_MS);
    },
    [onOpen],
  );

  return (
    <div ref={body} className="flex flex-col gap-3 min-h-0">
      <NewHere count={seen.waitingHere} glowing={seen.glowing} onJump={seen.next} onClearAll={seen.readAll} />
      {!empty && (
        <div className="shrink-0 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <label className="flex items-center gap-1.5">
            <span>Unit</span>
            <select
              aria-label="Unit"
              value={unit}
              onChange={e => setUnit(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-1 py-0.5 text-xs"
            >
              <option value="">all</option>
              {units.map(u => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </label>
          <span className="ml-auto text-gray-400">
            {data.totals.priced} of {data.totals.products} products priced · {data.totals.plans} plans
            {data.totals.unpublished ? ` · ${data.totals.unpublished} publish nothing` : ''}
          </span>
        </div>
      )}
      {data.groups.map(g => {
        const products = g.products.filter(keep);
        const max = Math.max(0, ...products.flatMap(p => p.plans.map(pl => pl.monthly ?? 0)));
        return (
          <div key={g.key} className={CARD} data-testid={`price-group-${g.key}`}>
            {/* ONE TOOLBAR PER TABLE, because this report draws a table per
                solution type: a single set of controls above them all could
                not say which table it acted on, and its download would have
                been a file nobody asked for, of everything at once. Each
                keeps its own memory for the same reason.

                THE HEADING GOES IN THE TABLE, not above it. Drawn separately,
                the controls landed under the price line and above the grid —
                floating in the middle of the card, belonging to neither. */}
            <DataTable
              table={`price-map-${g.key}`}
              file={`prices-${g.label}`}
              room={roomName}
              seen={seen}
              title={
                <span className="flex items-baseline gap-2 flex-wrap">
                  {g.solution_type_id ? (
                    <button type="button" onClick={() => onOpen(g.solution_type_id!)} className="text-sm font-semibold text-gray-800 dark:text-gray-100 hover:underline text-left">
                      {g.label}
                    </button>
                  ) : (
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{g.label}</span>
                  )}
                  <span className="text-xs text-gray-400">
                    {products.length} product{products.length === 1 ? '' : 's'}
                    {g.unit_mix.length > 1 && ` · priced ${g.unit_mix.join(', ')}`}
                  </span>
                </span>
              }
              beforeTable={max > 0 ? <Strip products={products} max={max} onPick={pick} /> : undefined}
              columns={columns}
              rows={products}
              rowKey={p => `price-row-${p.id}`}
            />
          </div>
        );
      })}
      <MissingList missing={data.missing} empty={empty} onOpen={onOpen} onDig={onDig} digging={digging} seen={seen} />
    </div>
  );
}
